"use client";

import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "../../lib/utils";

function Calendar({
  className,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-2 text-text", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-2",
        month_caption:
          "relative flex h-control w-full items-center justify-center",
        caption_label:
          "pointer-events-none text-xs font-semibold tracking-tight text-text",
        nav: "absolute inset-x-0 z-10 flex h-control items-center justify-between px-1",
        button_previous: cn(
          "inline-grid size-6 place-items-center rounded-sm bg-transparent p-0",
          "text-text-tertiary hover:bg-hover hover:text-text",
          "transition-[background-color,color] duration-100 ease-out-fast",
          "focus-visible:outline-none focus-fill",
          "disabled:pointer-events-none disabled:opacity-50",
        ),
        button_next: cn(
          "inline-grid size-6 place-items-center rounded-sm bg-transparent p-0",
          "text-text-tertiary hover:bg-hover hover:text-text",
          "transition-[background-color,color] duration-100 ease-out-fast",
          "focus-visible:outline-none focus-fill",
          "disabled:pointer-events-none disabled:opacity-50",
        ),
        weekdays: "flex",
        weekday:
          "w-8 text-xs font-medium uppercase tracking-wide text-text-tertiary",
        week: "mt-0.5 flex w-full",
        day: "relative size-8 p-0 text-center text-sm",
        day_button: cn(
          "size-8 rounded-sm p-0 text-sm font-normal text-text",
          "transition-[background-color,color] duration-100 ease-out-fast",
          "hover:bg-hover",
          "focus-visible:outline-none focus-fill",
          "aria-selected:bg-accent aria-selected:font-medium aria-selected:text-text-inverse",
          "aria-selected:hover:bg-accent-hover",
        ),
        range_start: "rounded-l-sm",
        range_end: "rounded-r-sm",
        range_middle: cn(
          "bg-accent-subtle",
          "[&>button]:aria-selected:bg-transparent",
          "[&>button]:aria-selected:text-text",
          "[&>button]:aria-selected:hover:bg-hover",
        ),
        selected: "[&>button]:bg-accent [&>button]:text-text-inverse",
        today: "[&>button]:font-semibold [&>button]:text-accent-text",
        outside: "[&>button]:text-text-disabled",
        disabled: "[&>button]:text-text-disabled [&>button]:opacity-50",
        hidden: "invisible",
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeftIcon className="size-3.5" />
          ) : (
            <ChevronRightIcon className="size-3.5" />
          ),
      }}
      {...props}
    />
  );
}

export { Calendar };
